import React, { useEffect, useRef, useState } from "react";
import Icon from "../components/common/icons";
import { openGetAppCard, shouldNudgeApp, shouldOfferApp } from "./installPrompt";
import { deviceWord, usePushState } from "./PushAsk";
import { PUSH, pushNeedsApp } from "./pushNotifications";
import "./pwa.css";

/**
 * The places that offer "Get the ANNA app" on a phone. Each one only opens the card
 * (InstallAppPrompt.js), which shows the right steps for that phone. None of them
 * show on computers or once ANNA is open as the app. The chat note can offer
 * notifications instead (PushAsk.js, pushNotifications.js).
 */

const tx = (lang, en, fil) => (lang === "fil" ? fil : en);

const NOTE_DELAY_MS = 1200;

/**
 * ANNA's message after the last test, under the finish card. The test chat only adds
 * it when every test is done and the answers saved; never after worrying answers.
 * `results` is the finish card's message ("in_review", "released" or "not_shared").
 * `Row` is the chat's own message row, so it looks like the rest of the chat.
 *
 * When the results come later (in_review) and this phone can get notifications, it
 * offers to send one when they're ready. Otherwise, on phones without the app, it
 * offers "Get the app" (on iPhone that's also the only way to get notifications).
 * Computers get neither (notifications are phones only; school computers are shared).
 */
export function GetAppChatNote({ lang = "en", results, Row }) {
  const resultsLater = results === "in_review";
  const push = usePushState(resultsLater);
  const [due, setDue] = useState(false);
  // Decided once, so the message doesn't change into another one
  const [kind, setKind] = useState(null);
  const [answer, setAnswer] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDue(true), NOTE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (kind || !due || (resultsLater && push.state === null)) return;
    if (resultsLater && push.state === PUSH.OFF) setKind("push");
    else if (shouldNudgeApp()) setKind("app");
    else setKind("none");
  }, [due, kind, resultsLater, push.state]);

  const show = kind === "push" || kind === "app";
  useEffect(() => {
    if (show) ref.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [show, answer]);

  if (!show) return null;

  if (kind === "push") {
    const device = deviceWord();
    const onIt = tx(lang, `on your ${device}`, `sa iyong ${device}`);
    const yes = () => {
      setAnswer("asking");
      push.turnOn().then((state) =>
        setAnswer(
          state === PUSH.ON ? "on" : state === PUSH.BLOCKED ? "blocked" : state === PUSH.FAILED ? "failed" : "later",
        ));
    };

    let text = tx(lang,
      `One more thing: your results come later. Want a message ${onIt} when they're ready?`,
      `Isa pa: mamaya pa lalabas ang iyong mga resulta. Gusto mo bang makatanggap ng mensahe ${onIt} kapag handa na ang mga ito?`);
    if (answer === "on") {
      text = tx(lang,
        `Done! We'll send a message ${onIt} when your results are ready.`,
        `Ayos! Magpapadala kami ng mensahe ${onIt} kapag handa na ang iyong mga resulta.`);
    } else if (answer === "blocked") {
      text = tx(lang,
        `Your ${device} is blocking messages from ANNA. You can allow them in your ${device}'s settings.`,
        `Hinaharangan ng iyong ${device} ang mga mensahe mula sa ANNA. Puwede mo itong payagan sa settings ng iyong ${device}.`);
    } else if (answer === "failed") {
      text = tx(lang,
        "That didn't work in this browser. You can try again anytime in your Profile.",
        "Hindi ito gumana sa browser na ito. Puwede mong subukan ulit kahit kailan sa iyong Profile.");
    } else if (answer === "later") {
      text = tx(lang,
        "Okay! You can turn this on anytime in your Profile.",
        "Sige! Puwede mo itong i-on kahit kailan sa iyong Profile.");
    }

    return (
      <Row>
        <div className="ts-bubble pwa-note" ref={ref} aria-live="polite">
          <span>{text}</span>
          {(answer === null || answer === "asking") && (
            <span className="push-note-actions">
              <button type="button" className="pwa-note-btn" onClick={yes} disabled={answer === "asking"}>
                <Icon name="bell" size={18} />
                {tx(lang, "Yes, tell me", "Oo, sabihan mo ako")}
              </button>
              <button type="button" className="push-note-skip" onClick={() => setAnswer("later")} disabled={answer === "asking"}>
                {tx(lang, "Not now", "Huwag muna")}
              </button>
            </span>
          )}
        </div>
      </Row>
    );
  }

  const text =
    results === "released"
      ? tx(lang,
        "One more thing: get the ANNA app so your results are one tap away.",
        "Isa pa: kunin ang ANNA app para isang tap na lang ang iyong mga resulta.")
      : results === "not_shared"
        ? tx(lang,
          "One more thing: get the ANNA app so it's easy to come back anytime.",
          "Isa pa: kunin ang ANNA app para madali kang makabalik kahit kailan.")
        : push.state === PUSH.NEEDS_APP
          ? tx(lang,
            "One more thing: your results come later. Get the ANNA app and it can tell you when they're ready.",
            "Isa pa: mamaya pa lalabas ang iyong mga resulta. Kunin ang ANNA app para masabihan ka nito kapag handa na ang mga ito.")
          : tx(lang,
            "One more thing: your results come later. Get the ANNA app so it's easy to check back.",
            "Isa pa: mamaya pa lalabas ang iyong mga resulta. Kunin ang ANNA app para madali kang makabalik.");

  return (
    <Row>
      <div className="ts-bubble pwa-note" ref={ref}>
        <span>{text}</span>
        <button type="button" className="pwa-note-btn" onClick={openGetAppCard}>
          <Icon name="smartphone" size={18} />
          {tx(lang, "Get the app", "Kunin ang app")}
        </button>
      </div>
    </Row>
  );
}

/** A strip on the "You're booked" card. On iPhone it's also how to get the reminder. */
export function GetAppStrip() {
  const [show] = useState(shouldNudgeApp);
  if (!show) return null;
  return (
    <div className="pwa-strip">
      <span className="pwa-strip-tile" aria-hidden="true">
        <Icon name="smartphone" size={22} />
      </span>
      <span className="pwa-strip-text">
        {pushNeedsApp()
          ? "Get the ANNA app and it can remind you before your talk."
          : "Keep your talk one tap away. Get the ANNA app."}
      </span>
      <button type="button" className="pwa-strip-btn" onClick={openGetAppCard}>
        Get it
      </button>
    </div>
  );
}

/** A small button under the landing page's main buttons, signed in or not. */
export function GetAppLandingButton() {
  const [show] = useState(shouldOfferApp);
  if (!show) return null;
  return (
    <button type="button" className="pwa-landing-btn" onClick={openGetAppCard}>
      <Icon name="smartphone" size={17} />
      Get the ANNA app
    </button>
  );
}
